import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Menu, ChevronDown, User as UserIcon, LogOut, Zap, Shield, ShieldCheck, KeyRound, Home, LayoutDashboard, MoreHorizontal } from 'lucide-react';
import { Button } from '@/components/ui';
import { useAuthStore } from '@/stores/auth';
import { api } from '@/lib/api';
import { NAV_MAIN, NAV_MORE, ROLE_LABEL, ROLE_COLOR } from '@/lib/constants';
import { cn } from '@/lib/utils';

export function Header({ onToggleSidebar }: { onToggleSidebar?: () => void }) {
  const user = useAuthStore((s) => s.user);
  const reset = useAuthStore((s) => s.reset);
  const qc = useQueryClient();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);

  // admin 비밀번호 세션 — 회원 로그인과 별개이지만 헤더에서 같은 위치에 표시
  const adminSession = useQuery({
    queryKey: ['admin', 'me'],
    queryFn: () => api.adminAuth.me(),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
  const adminAuthed = adminSession.data?.authenticated ?? false;

  const adminLogout = useMutation({
    mutationFn: () => api.adminAuth.logout(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin'] }),
  });

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
      if (!moreRef.current?.contains(e.target as Node)) setMoreOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  // 현재 경로가 "더보기"에 속하는지 — 활성 강조용
  const location_ = useLocation();
  const moreActive = NAV_MORE.some((n) => location_.pathname.startsWith(n.to) && n.to !== '/');

  // 우측 표시 모드: user(회원/운영진/관리자) > admin 세션만 > 비인증
  const mode: 'user' | 'admin-only' | 'guest' = user ? 'user' : adminAuthed ? 'admin-only' : 'guest';

  // 홈/대시보드 토글: 로그인 사용자가 Landing 페이지(/landing)를 보고 있으면 "대시보드"
  //                  그 외에는 "홈" (랜딩으로 이동)
  const location = useLocation();
  const onLanding = location.pathname === '/landing' || (location.pathname === '/' && !user);

  return (
    <header className="sticky top-0 z-40 h-16 border-b border-gray-800 bg-[#030712]/85 backdrop-blur supports-[backdrop-filter]:bg-[#030712]/70">
      <div className="mx-auto flex h-full max-w-7xl items-center gap-4 px-4 sm:px-6">
        {/* 모바일 사이드바 토글 */}
        <button
          type="button"
          onClick={onToggleSidebar}
          className="md:hidden inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-300 hover:bg-gray-900"
          aria-label="사이드바 열기"
        >
          <Menu className="h-5 w-5" />
        </button>

        {/* 로고 */}
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <Zap className="h-5 w-5 text-gold" aria-hidden="true" />
          <span className="text-lg font-extrabold text-gold-glow tracking-tight">KeywordGod</span>
        </Link>

        {/* 데스크톱 네비 (Phase 45 — 주요 5개 + 더보기 드롭다운) */}
        <nav className="hidden md:flex items-center gap-1 ml-6">
          {NAV_MAIN.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) =>
                cn(
                  'inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-[15px] font-semibold transition-colors',
                  isActive
                    ? 'text-gold bg-gold/15 ring-1 ring-gold/30'
                    : 'text-gray-200 hover:text-white hover:bg-gray-900',
                )
              }
            >
              <n.icon className="h-4 w-4" />
              <span>{n.label}</span>
            </NavLink>
          ))}

          {/* 더보기 드롭다운 */}
          <div ref={moreRef} className="relative">
            <button
              type="button"
              onClick={() => setMoreOpen((o) => !o)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-[15px] font-semibold transition-colors',
                moreActive
                  ? 'text-gold bg-gold/15 ring-1 ring-gold/30'
                  : 'text-gray-200 hover:text-white hover:bg-gray-900',
              )}
            >
              <MoreHorizontal className="h-4 w-4" />
              <span>더보기</span>
              <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', moreOpen && 'rotate-180')} />
            </button>
            {moreOpen && (
              <div className="absolute left-0 mt-2 w-56 overflow-hidden rounded-xl border border-gray-800 bg-gray-900 shadow-2xl z-50">
                {(() => {
                  const groups = Array.from(new Set(NAV_MORE.map((n) => n.group)));
                  return groups.map((g) => (
                    <div key={g} className="py-1">
                      <div className="px-3 py-1 text-[10px] uppercase tracking-widest text-gray-500">
                        {g}
                      </div>
                      {NAV_MORE.filter((n) => n.group === g).map((n) => (
                        <NavLink
                          key={n.to}
                          to={n.to}
                          onClick={() => setMoreOpen(false)}
                          className={({ isActive }) =>
                            cn(
                              'flex items-center gap-2.5 px-3 py-2 text-sm',
                              isActive
                                ? 'text-gold bg-gold/10 font-semibold'
                                : 'text-gray-200 hover:bg-gray-800',
                            )
                          }
                        >
                          <n.icon className="h-4 w-4 text-gold/70" />
                          <span>{n.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  ));
                })()}
              </div>
            )}
          </div>
        </nav>

        <div className="flex-1" />

        {/* 홈 / 대시보드 토글 버튼 (로그인 사용자에게만 노출) */}
        {user && (
          <Link
            to={onLanding ? '/' : '/landing'}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
              onLanding
                ? 'bg-gold text-black hover:bg-gold/90'
                : 'border border-gold/30 text-gold hover:bg-gold/10',
            )}
            aria-label={onLanding ? '대시보드로 돌아가기' : '랜딩 페이지로'}
            title={onLanding ? '대시보드로 돌아가기' : '랜딩 페이지로'}
          >
            {onLanding ? (
              <>
                <LayoutDashboard className="h-4 w-4" />
                <span className="hidden sm:inline">대시보드</span>
              </>
            ) : (
              <>
                <Home className="h-4 w-4" />
                <span className="hidden sm:inline">홈</span>
              </>
            )}
          </Link>
        )}

        {/* 우측: 인증 상태 분기 */}
        {mode === 'guest' && (
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="hidden sm:inline-block text-xs text-gray-400 hover:text-gold"
            >
              회원가입
            </Link>
            <Button size="md" variant="gold" onClick={() => api.auth.googleStart()}>
              Google 로그인
            </Button>
          </div>
        )}

        {mode === 'user' && user && (
          <div ref={menuRef} className="relative">
            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              className="flex items-center gap-2 rounded-full bg-gray-900 px-2 py-1 hover:bg-gray-800 transition-colors"
            >
              <Avatar src={user.picture} name={user.nickname || user.name} />
              <span className="hidden sm:block text-sm font-medium text-white">
                {user.nickname || user.name}
              </span>
              {adminAuthed && (
                <span className="hidden sm:inline-flex items-center rounded-full bg-gold/15 px-1.5 py-0.5 text-[10px] font-bold text-gold">
                  관리자
                </span>
              )}
              <ChevronDown className="h-4 w-4 text-gray-400" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 mt-2 w-64 overflow-hidden rounded-xl border border-gray-800 bg-gray-900 shadow-2xl">
                <div className="px-4 py-3 border-b border-gray-800">
                  <div className="text-sm font-semibold text-white truncate">
                    {user.nickname || user.name}
                  </div>
                  <div className="text-xs text-gray-400 truncate">{user.email}</div>
                  <div className="mt-2 flex items-center gap-1 flex-wrap">
                    <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-bold', ROLE_COLOR[user.role])}>
                      {ROLE_LABEL[user.role]}
                    </span>
                    {adminAuthed && (
                      <span className="rounded-full bg-gold/15 px-2 py-0.5 text-[11px] font-bold text-gold">
                        관리자 인증
                      </span>
                    )}
                  </div>
                </div>
                <MenuItem icon={UserIcon} to="/settings" label="내정보" onSelect={() => setMenuOpen(false)} />
                {(user.role === 'moderator' || user.role === 'admin') && (
                  <MenuItem icon={ShieldCheck} to="/mod" label="회원 승인" onSelect={() => setMenuOpen(false)} />
                )}
                {user.role === 'admin' && (
                  <MenuItem icon={Shield} to="/admin" label="관리자 콘솔" onSelect={() => setMenuOpen(false)} />
                )}
                {adminAuthed && (
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-amber-300 hover:bg-gray-800"
                    onClick={() => {
                      setMenuOpen(false);
                      adminLogout.mutate();
                    }}
                  >
                    <KeyRound className="h-4 w-4" />
                    관리자 로그아웃
                  </button>
                )}
                <button
                  type="button"
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-red-400 hover:bg-gray-800"
                  onClick={async () => {
                    setMenuOpen(false);
                    try {
                      await api.auth.logout();
                    } finally {
                      reset();
                      window.location.href = '/';
                    }
                  }}
                >
                  <LogOut className="h-4 w-4" />
                  로그아웃
                </button>
              </div>
            )}
          </div>
        )}

        {mode === 'admin-only' && (
          <div ref={menuRef} className="relative">
            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              className="flex items-center gap-2 rounded-full bg-gray-900 px-2 py-1 hover:bg-gray-800 transition-colors"
            >
              <AdminAvatar />
              <span className="hidden sm:block text-sm font-medium text-white">관리자</span>
              <span className="rounded-full bg-gold/15 px-1.5 py-0.5 text-[10px] font-bold text-gold">
                ADMIN
              </span>
              <ChevronDown className="h-4 w-4 text-gray-400" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 mt-2 w-64 overflow-hidden rounded-xl border border-gray-800 bg-gray-900 shadow-2xl">
                <div className="px-4 py-3 border-b border-gray-800">
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-gold" />
                    <span className="text-sm font-semibold text-white">관리자 세션</span>
                  </div>
                  <div className="mt-1 text-xs text-gray-400">
                    Google 로그인이 별도로 필요한 페이지는 회원 로그인을 진행하세요.
                  </div>
                </div>
                <MenuItem
                  icon={Shield}
                  to="/admin"
                  label="관리자 콘솔"
                  onSelect={() => setMenuOpen(false)}
                />
                <button
                  type="button"
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-gray-200 hover:bg-gray-800"
                  onClick={() => {
                    setMenuOpen(false);
                    api.auth.googleStart();
                  }}
                >
                  <UserIcon className="h-4 w-4 text-gold" />
                  Google 회원 로그인
                </button>
                <button
                  type="button"
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-amber-300 hover:bg-gray-800"
                  onClick={() => {
                    setMenuOpen(false);
                    adminLogout.mutate();
                  }}
                >
                  <KeyRound className="h-4 w-4" />
                  관리자 로그아웃
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}

function MenuItem({
  icon: Icon,
  to,
  label,
  onSelect,
}: {
  icon: typeof UserIcon;
  to: string;
  label: string;
  onSelect: () => void;
}) {
  return (
    <Link
      to={to}
      onClick={onSelect}
      className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-200 hover:bg-gray-800"
    >
      <Icon className="h-4 w-4 text-gold" />
      {label}
    </Link>
  );
}

function Avatar({ src, name }: { src?: string; name: string }) {
  if (src) {
    return (
      <img
        src={src}
        alt={name}
        className="h-7 w-7 rounded-full ring-1 ring-gold/40 object-cover"
        referrerPolicy="no-referrer"
      />
    );
  }
  return (
    <div className="h-7 w-7 rounded-full bg-gradient-to-br from-[#FFD700] to-[#FFA500] text-black text-xs font-bold flex items-center justify-center">
      {name.slice(0, 1).toUpperCase()}
    </div>
  );
}

function AdminAvatar() {
  return (
    <div className="h-7 w-7 rounded-full bg-gradient-to-br from-[#FFD700] to-[#FFA500] text-black flex items-center justify-center ring-1 ring-gold/40">
      <Shield className="h-4 w-4" />
    </div>
  );
}
